import { useCallback, useEffect, useRef } from "react";

import type {
  BackgroundRemovalJob,
  BackgroundRemovalJobResult,
} from "../editor/types";

interface PendingRequest {
  resolve: (imageData: ImageData) => void;
  reject: (error: Error) => void;
}

export function useBackgroundRemovalProcessor() {
  const workerRef = useRef<Worker | null>(null);
  const requestIdRef = useRef(0);
  const pendingRef = useRef(new Map<number, PendingRequest>());

  useEffect(() => {
    const worker = new Worker(
      new URL("../workers/backgroundRemovalWorker.ts", import.meta.url),
      { type: "module" },
    );
    workerRef.current = worker;
    worker.onmessage = (event: MessageEvent<BackgroundRemovalJobResult>) => {
      const pending = pendingRef.current.get(event.data.requestId);
      if (!pending) return;
      pendingRef.current.delete(event.data.requestId);
      if (event.data.error || !event.data.imageData) {
        pending.reject(
          new Error(event.data.error ?? "背景透過結果を取得できませんでした"),
        );
        return;
      }
      pending.resolve(event.data.imageData);
    };
    worker.onerror = () => {
      pendingRef.current.forEach(({ reject }) =>
        reject(new Error("背景透過ワーカーが停止しました")),
      );
      pendingRef.current.clear();
    };
    return () => {
      worker.terminate();
      workerRef.current = null;
      pendingRef.current.forEach(({ reject }) =>
        reject(new Error("背景透過がキャンセルされました")),
      );
      pendingRef.current.clear();
    };
  }, []);

  return useCallback((imageData: ImageData): Promise<ImageData> => {
    const worker = workerRef.current;
    if (!worker) {
      return Promise.reject(
        new Error("背景透過ワーカーを初期化できませんでした"),
      );
    }
    const requestId = ++requestIdRef.current;
    const copiedData = new Uint8ClampedArray(imageData.data);
    const transferableImage = new ImageData(
      copiedData,
      imageData.width,
      imageData.height,
    );
    const job: BackgroundRemovalJob = {
      requestId,
      imageData: transferableImage,
    };
    return new Promise((resolve, reject) => {
      pendingRef.current.set(requestId, { resolve, reject });
      worker.postMessage(job, [copiedData.buffer]);
    });
  }, []);
}
